import { FC, ReactNode, useEffect, useState } from "react";
import { IntlProvider } from "react-intl";
import { useRecoilValue } from "recoil";
import { currentUserAtom } from "./AuthProvider";
import { Locale } from "./Locale";

/**
 * Represents the different industries
 *
 * For now, the values match the label as we don't have code.
 * TODO: consider adding codes or using ids
 *  ids are less readable when debugging though
 */
export enum Industry {
  BOAT = "Bateau",
  CAMPING = "Camping",
  CRUISE = "Croisière",
  FERRY = "Ferry",
  HOTEL = "Hôtellerie",
  OTHER = "Autre",
  PARK = "Parc",
  THEATRE = "Theatre",
}

export const BROWSER_INDUSTRIES: {
  [key: string]: Industry;
} = {
  boat: Industry.BOAT,
  camping: Industry.CAMPING,
  cruise: Industry.CRUISE,
  ferry: Industry.FERRY,
  hotel: Industry.HOTEL,
  other: Industry.OTHER,
  park: Industry.PARK,
  theatre: Industry.THEATRE,
};

export const DEFAULT_INDUSTRY = Industry.HOTEL;

/**
 * Returns the industry if set as url param
 *
 * We can override using the queryParam `industry`.
 * Otherwise, we return null so that we use the tenant when
 * currentUser is set.
 */
export const getCurrentIndustry = (): Industry | null => {
  const queryParams = new URLSearchParams(window.location.search);
  const industry = queryParams.get("industry");
  return (industry && BROWSER_INDUSTRIES[industry]) || null;
};

const LOCALE_TO_FILE: Record<Locale, string> = {
  [Locale.CA_ES]: "ca",
  [Locale.EN_GB]: "en",
  [Locale.EN_US]: "en",
  [Locale.ES_ES]: "es",
  [Locale.FR_FR]: "fr",
  [Locale.IT_IT]: "it",
  [Locale.DE_DE]: "de",
};

const INDUSTRY_TO_FILE: Record<Industry, string> = {
  [Industry.BOAT]: "boat",
  [Industry.CAMPING]: "camping",
  [Industry.CRUISE]: "cruise",
  [Industry.FERRY]: "ferry",
  [Industry.HOTEL]: "hotel",
  [Industry.OTHER]: "other",
  [Industry.PARK]: "park",
  [Industry.THEATRE]: "theatre",
};

/**
 * Dynamically loads only the translations needed for the current
 * locale + industry combination, instead of bundling all 54 files.
 *
 * Webpack splits each JSON file into its own chunk loaded on demand.
 *
 * Base translations are always loaded first.
 * The industry overlay is only loaded once the real industry is known
 * (i.e. industry is not null), avoiding an eager load of the hotel
 * fallback on every mount before currentUser hydrates.
 * If the industry file fails, base translations are used as fallback
 * so the app never renders blank.
 */
const loadTranslations = async (
  locale: Locale,
  industry: Industry | null
): Promise<Record<string, string>> => {
  const localeFile = LOCALE_TO_FILE[locale];

  if (!industry) {
    return (await import(`./Translations/${localeFile}.json`)).default;
  }

  const industryFile = INDUSTRY_TO_FILE[industry];

  const [base, industryTrad] = await Promise.all([
    import(`./Translations/${localeFile}.json`).then((m) => m.default),
    import(`./Translations/${localeFile}/${industryFile}.json`)
      .then((m) => m.default)
      .catch(() => ({})), // fallback to base if industry file is missing
  ]);

  return { ...base, ...industryTrad };
};

type IndustryIntlProviderProps = {
  children?: ReactNode;
  locale: Locale;
};

export const IndustryIntlProvider: FC<IndustryIntlProviderProps> = ({
  children,
  locale,
}: IndustryIntlProviderProps) => {
  const currentUser = useRecoilValue(currentUserAtom);

  /**
   * Industry is nullable so that we do not update with the user value
   * when it was overriden with the url parameter.
   */
  const [industry, setIndustry] = useState<Industry | null>(() =>
    getCurrentIndustry()
  );
  const [translations, setTranslations] = useState<Record<
    string,
    string
  > | null>(null);

  /**
   * Updates the industry when we get the currentUser
   *
   * In case the industry was passed as an url param, we do not update it.
   * This allows us to test different industries without matching the database.
   */
  useEffect(() => {
    if (currentUser && !industry) {
      const newIndustry = currentUser.tenant.industry;
      setIndustry((prev) => (prev !== newIndustry ? newIndustry : prev));
    }
  }, [currentUser, industry]);

  /**
   * Loads translations dynamically when locale or industry change.
   * The cancelled flag prevents a stale response from overwriting
   * a more recent one if locale/industry changes before the fetch resolves.
   */
  useEffect(() => {
    let cancelled = false;
    loadTranslations(locale, industry)
      .then((t) => {
        if (!cancelled) setTranslations(t);
      })
      .catch(() => {
        if (!cancelled)
          console.error("Failed to load translations", { locale, industry });
      });
    return () => {
      cancelled = true;
    };
  }, [locale, industry]);

  if (!translations) return null;

  return (
    <IntlProvider
      locale={locale}
      messages={translations}
      onError={(err) => {
        if (err.code === "MISSING_TRANSLATION") {
          console.warn("Missing translation", err.message);
          return;
        }
        throw err;
      }}
    >
      {children}
    </IntlProvider>
  );
};
